import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Pagination, Tooltip
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import PaletteIcon from "@mui/icons-material/Palette";
import InfoIcon from "@mui/icons-material/Info";
import { fetchData, postData, putData, deleteData } from "../../../BaseUrl";
import Toast from "../../../DynamicComponent/Toast";

const PAGE_SIZE = 10;

function ConfirmDialog({ open, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "16px" }}>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "14px", color: "#374151" }}>
          Are you sure you want to delete this item?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="error" sx={{ textTransform: "none", borderRadius: "8px" }}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const btnStyle = {
  background: "linear-gradient(90deg, #4A7BA8, #244C77)",
  color: "#fff", fontSize: "13px", textTransform: "none",
  borderRadius: "10px", px: 2.5, py: 1,
  "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
};

const navBtnStyle = {
  background: "linear-gradient(90deg, #4A7BA8, #244C77)",
  color: "#fff", fontSize: "13px", textTransform: "none",
  borderRadius: "10px", px: 3, py: 1,
  "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
};

// Step indicator
function StepIndicator({ step }) {
  const steps = ["Images", "Descriptions", "Icons / Counts"];
  return (
    <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
      {steps.map((label, i) => {
        const num = i + 1;
        const active = num === step;
        const done = num < step;
        return (
          <Box key={num} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: "50%",
              background: active || done ? "linear-gradient(90deg, #4A7BA8, #244C77)" : "#E5E7EB",
              color: active || done ? "#fff" : "#6B7280",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "13px", fontWeight: 700,
            }}>
              {num}
            </Box>
            <Typography sx={{ fontSize: "13px", fontWeight: active ? 700 : 400, color: active ? "#244C77" : "#6B7280" }}>
              {label}
            </Typography>
            {i < steps.length - 1 && (
              <Box sx={{ width: 32, height: 2, backgroundColor: done ? "#244C77" : "#E5E7EB", mx: 0.5 }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

const IMAGES_FALLBACK = [
  { _id: "1", imageUrl: "" },
  { _id: "2", imageUrl: "" },
  { _id: "3", imageUrl: "" },
];

const DESCRIPTIONS_FALLBACK = [
  { _id: "1", tamilHeader: "தமிழ் தலைப்பு", englishDescription: "English description about the school and its mission to provide quality Tamil education." },
  { _id: "2", tamilHeader: "நோக்கம்", englishDescription: "Our vision is to nurture young minds with the richness of Tamil language and culture." },
];

const ICONS_FALLBACK = [
  { _id: "1", iconUrl: "", count: "500+", color: "#244C77" },
  { _id: "2", iconUrl: "", count: "20+", color: "#4A7BA8" },
  { _id: "3", iconUrl: "", count: "10+", color: "#244C77" },
];

/* ─────────────────────────── STEP 1: Images ─────────────────────────── */
function Step1Images({ onNext }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/about-us/images/list");
      setRows(Array.isArray(res?.data) ? res.data : IMAGES_FALLBACK);
    } catch { setRows(IMAGES_FALLBACK); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditId(null); setImageFile(null); setImagePreview(""); setDialogOpen(true); };
  const openEdit = (row) => { setEditId(row._id || row.id); setImagePreview(row.imageUrl || ""); setImageFile(null); setDialogOpen(true); };
  const closeDialog = () => { setDialogOpen(false); setEditId(null); setImageFile(null); setImagePreview(""); };

  const handleSave = async () => {
    const fd = new FormData();
    if (imageFile) fd.append("image", imageFile);
    try {
      if (editId) { await putData(`admin/about-us/images/update/${editId}`, fd); }
      else { await postData("admin/about-us/images/create", fd); }
      setToast({ open: true, severity: "success", message: editId ? "Image updated." : "Image added." });
      closeDialog(); load();
    } catch { setToast({ open: true, severity: "error", message: "Failed to save." }); }
  };

  const handleDelete = async () => {
    const id = deleteTarget?._id || deleteTarget?.id;
    setConfirmOpen(false);
    try {
      await deleteData(`admin/about-us/images/${id}`);
      setToast({ open: true, severity: "success", message: "Image deleted." });
      load();
    } catch { setToast({ open: true, severity: "error", message: "Failed to delete." }); }
  };

  const pageData = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button startIcon={<AddPhotoAlternateIcon />} onClick={openAdd} sx={btnStyle}>+ Add Image</Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["S.No", "Image", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, color: "#9CA3AF" }}>No images yet.</TableCell>
                </TableRow>
              ) : pageData.map((row, idx) => (
                <TableRow key={row._id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" }, backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                  <TableCell sx={{ fontSize: "13px", width: 60 }}>{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                  <TableCell>
                    {row.imageUrl ? (
                      <img src={row.imageUrl} alt="about" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: "8px", border: "1px solid #E5E7EB" }} />
                    ) : (
                      <Box sx={{ width: 60, height: 60, borderRadius: "8px", backgroundColor: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed #D1D5DB" }}>
                        <AddPhotoAlternateIcon sx={{ color: "#9CA3AF", fontSize: "20px" }} />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "#244C77" }}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteTarget(row); setConfirmOpen(true); }} sx={{ color: "#EF4444" }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length > PAGE_SIZE && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={Math.ceil(rows.length / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" />
          </Box>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button onClick={onNext} sx={navBtnStyle}>Next →</Button>
      </Box>

      {/* Image Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px" }}>{editId ? "Edit Image" : "Add Image"}</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <Box component="label" sx={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            border: "2px dashed #D1D5DB", borderRadius: "10px", height: 180, cursor: "pointer",
            backgroundColor: "#F9FAFB", "&:hover": { borderColor: "#4A7BA8" }, overflow: "hidden",
          }}>
            {imagePreview ? (
              <img src={imagePreview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <>
                <AddPhotoAlternateIcon sx={{ fontSize: "40px", color: "#9CA3AF", mb: 1 }} />
                <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Click to upload image</Typography>
              </>
            )}
            <input type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={e => {
              const f = e.target.files[0]; if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }
            }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ ...btnStyle, px: 3, py: 0.8 }}>{editId ? "Update" : "Add"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ─────────────────────────── STEP 2: Descriptions ─────────────────────────── */
function Step2Descriptions({ onBack, onNext }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ tamilHeader: "", englishDescription: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/about-us/descriptions/list");
      setRows(Array.isArray(res?.data) ? res.data : DESCRIPTIONS_FALLBACK);
    } catch { setRows(DESCRIPTIONS_FALLBACK); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditId(null); setForm({ tamilHeader: "", englishDescription: "" }); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditId(row._id || row.id);
    setForm({ tamilHeader: row.tamilHeader || "", englishDescription: row.englishDescription || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editId) { await putData(`admin/about-us/descriptions/update/${editId}`, form); }
      else { await postData("admin/about-us/descriptions/create", form); }
      setToast({ open: true, severity: "success", message: editId ? "Description updated." : "Description added." });
      setDialogOpen(false); load();
    } catch { setToast({ open: true, severity: "error", message: "Failed to save." }); }
  };

  const handleDelete = async () => {
    const id = deleteTarget?._id || deleteTarget?.id;
    setConfirmOpen(false);
    try {
      await deleteData(`admin/about-us/descriptions/${id}`);
      setToast({ open: true, severity: "success", message: "Deleted." });
      load();
    } catch { setToast({ open: true, severity: "error", message: "Failed to delete." }); }
  };

  const pageData = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button startIcon={<InfoIcon />} onClick={openAdd} sx={btnStyle}>+ Add Description</Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["S.No", "Tamil Header", "English Description", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: "#9CA3AF" }}>No descriptions yet.</TableCell></TableRow>
              ) : pageData.map((row, idx) => (
                <TableRow key={row._id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" }, backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                  <TableCell sx={{ fontSize: "13px", width: 60 }}>{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                  <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#374151", maxWidth: 180 }}>
                    <Typography noWrap sx={{ fontSize: "13px", maxWidth: 160 }}>{row.tamilHeader}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 350 }}>
                    <Typography noWrap sx={{ fontSize: "13px", color: "#6B7280", maxWidth: 330 }}>{row.englishDescription}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "#244C77" }}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteTarget(row); setConfirmOpen(true); }} sx={{ color: "#EF4444" }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length > PAGE_SIZE && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={Math.ceil(rows.length / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" />
          </Box>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button onClick={onBack} sx={{ ...navBtnStyle, background: "#F3F4F6", color: "#374151", "&:hover": { background: "#E5E7EB" } }}>← Back</Button>
        <Button onClick={onNext} sx={navBtnStyle}>Next →</Button>
      </Box>

      {/* Description Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px" }}>{editId ? "Edit Description" : "Add Description"}</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <TextField label="Tamil Header" fullWidth size="small" value={form.tamilHeader}
            onChange={e => setForm(p => ({ ...p, tamilHeader: e.target.value }))}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField label="English Description" fullWidth size="small" multiline rows={4} value={form.englishDescription}
            onChange={e => setForm(p => ({ ...p, englishDescription: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ ...btnStyle, px: 3, py: 0.8 }}>{editId ? "Update" : "Add"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ─────────────────────────── STEP 3: Icons / Counts ─────────────────────────── */
function Step3Icons({ onBack }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ count: "", color: "#244C77", imageFile: null, imagePreview: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/about-us/icons/list");
      setRows(Array.isArray(res?.data) ? res.data : ICONS_FALLBACK);
    } catch { setRows(ICONS_FALLBACK); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditId(null); setForm({ count: "", color: "#244C77", imageFile: null, imagePreview: "" }); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditId(row._id || row.id);
    setForm({ count: row.count || "", color: row.color || "#244C77", imageFile: null, imagePreview: row.iconUrl || "" });
    setDialogOpen(true);
  };

  const handleColorChange = async (row, color) => {
    const id = row._id || row.id;
    setRows(prev => prev.map(r => (r._id === id || r.id === id) ? { ...r, color } : r));
    try {
      await putData(`admin/about-us/icons/update/${id}`, { color });
      setToast({ open: true, severity: "success", message: "Color updated." });
    } catch { setToast({ open: true, severity: "error", message: "Failed to update color." }); }
  };

  const handleSave = async () => {
    const fd = new FormData();
    fd.append("count", form.count);
    fd.append("color", form.color);
    if (form.imageFile) fd.append("icon", form.imageFile);
    try {
      if (editId) { await putData(`admin/about-us/icons/update/${editId}`, fd); }
      else { await postData("admin/about-us/icons/create", fd); }
      setToast({ open: true, severity: "success", message: editId ? "Icon updated." : "Icon added." });
      setDialogOpen(false); load();
    } catch { setToast({ open: true, severity: "error", message: "Failed to save." }); }
  };

  const handleDelete = async () => {
    const id = deleteTarget?._id || deleteTarget?.id;
    setConfirmOpen(false);
    try {
      await deleteData(`admin/about-us/icons/${id}`);
      setToast({ open: true, severity: "success", message: "Deleted." });
      load();
    } catch { setToast({ open: true, severity: "error", message: "Failed to delete." }); }
  };

  const handleUpdate = async () => {
    setToast({ open: true, severity: "success", message: "Icons/Counts configuration saved." });
  };

  const pageData = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button startIcon={<AddPhotoAlternateIcon />} onClick={openAdd} sx={btnStyle}>+ Add Icon</Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["S.No", "Icon", "Count", "Color", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "#9CA3AF" }}>No icons yet.</TableCell></TableRow>
              ) : pageData.map((row, idx) => (
                <TableRow key={row._id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" }, backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                  <TableCell sx={{ fontSize: "13px", width: 60 }}>{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                  <TableCell>
                    {row.iconUrl ? (
                      <img src={row.iconUrl} alt="icon" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: "8px", border: "1px solid #E5E7EB" }} />
                    ) : (
                      <Box sx={{ width: 60, height: 60, borderRadius: "8px", backgroundColor: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed #D1D5DB" }}>
                        <AddPhotoAlternateIcon sx={{ color: "#9CA3AF", fontSize: "20px" }} />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", fontWeight: 700, color: "#374151" }}>{row.count}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: row.color || "#244C77", border: "2px solid #E5E7EB" }} />
                      <Tooltip title="Change color">
                        <IconButton size="small" component="label" sx={{ color: "#6B7280" }}>
                          <PaletteIcon fontSize="small" />
                          <input type="color" hidden value={row.color || "#244C77"}
                            onChange={e => handleColorChange(row, e.target.value)} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "#244C77" }}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteTarget(row); setConfirmOpen(true); }} sx={{ color: "#EF4444" }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length > PAGE_SIZE && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={Math.ceil(rows.length / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" />
          </Box>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button onClick={onBack} sx={{ ...navBtnStyle, background: "#F3F4F6", color: "#374151", "&:hover": { background: "#E5E7EB" } }}>← Back</Button>
        <Button onClick={handleUpdate} sx={navBtnStyle}>Update</Button>
      </Box>

      {/* Icon Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px" }}>{editId ? "Edit Icon" : "Add Icon"}</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <Box component="label" sx={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            border: "2px dashed #D1D5DB", borderRadius: "10px", height: 160, cursor: "pointer",
            backgroundColor: "#F9FAFB", "&:hover": { borderColor: "#4A7BA8" }, overflow: "hidden", mb: 2,
          }}>
            {form.imagePreview ? (
              <img src={form.imagePreview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <>
                <AddPhotoAlternateIcon sx={{ fontSize: "40px", color: "#9CA3AF", mb: 1 }} />
                <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Click to upload icon</Typography>
              </>
            )}
            <input type="file" hidden accept=".jpg,.jpeg,.png,.webp,.svg" onChange={e => {
              const f = e.target.files[0]; if (f) setForm(p => ({ ...p, imageFile: f, imagePreview: URL.createObjectURL(f) }));
            }} />
          </Box>
          <TextField label="Count" fullWidth size="small" value={form.count}
            onChange={e => setForm(p => ({ ...p, count: e.target.value }))}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} placeholder="e.g. 500+" />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>Color</Typography>
            <input type="color" value={form.color}
              onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
              style={{ width: 40, height: 36, border: "none", borderRadius: "8px", cursor: "pointer" }} />
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>{form.color}</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ ...btnStyle, px: 3, py: 0.8 }}>{editId ? "Update" : "Add"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ─────────────────────────── MAIN COMPONENT ─────────────────────────── */
function AboutUsConfig() {
  const [step, setStep] = useState(1);

  const stepTitles = {
    1: { title: "About Us — Images", sub: "Manage images displayed in the About Us section" },
    2: { title: "About Us — Descriptions", sub: "Manage Tamil header and English description content" },
    3: { title: "About Us — Icons & Counts", sub: "Manage statistic icons with counts and colors" },
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 12px" }}>
        About Us / About Us displayed in the website
      </p>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <InfoIcon sx={{ color: "#244C77", fontSize: "28px" }} />
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>
            {stepTitles[step].title}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>{stepTitles[step].sub}</Typography>
        </Box>
      </Box>

      <StepIndicator step={step} />

      {step === 1 && <Step1Images onNext={() => setStep(2)} />}
      {step === 2 && <Step2Descriptions onBack={() => setStep(1)} onNext={() => setStep(3)} />}
      {step === 3 && <Step3Icons onBack={() => setStep(2)} />}
    </div>
  );
}

export default AboutUsConfig;
